import { useEffect, useState } from "react";
import { useUserContext } from "@/shared/features/user-context/presentation/hooks/useUserContext";
import {
  generateOtpForVerification,
  verifyContactDetail,
  resendOtpForVerification
} from "../../../account-merging/application/services/accountMergingService";

export type VerifyPhoneStep = "PHONE" | "OTP";

export function useVerifyPhoneNumber() {
  const { userContext } = useUserContext();
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState<VerifyPhoneStep>("PHONE");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpId, setOtpId] = useState<number | undefined>();
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const shouldOpen = userContext?.isLoggedIn && !userContext?.hasPhoneNumber;

  useEffect(() => {
    if (shouldOpen) {
      setOpen(true);
    } else {
      setOpen(false);
      setStep("PHONE");
      setPhoneNumber("");
      setErrorMessage("");
      setSuccessMessage("");
    }
  }, [shouldOpen]);

  const handlePhoneChange = (value: string) => {
    if (/^\d*$/.test(value) && value.length <= 10) {
      setPhoneNumber(value);
      setErrorMessage("");
    }
  };

  const sendOtp = async () => {
    if (phoneNumber.length !== 10) {
      setErrorMessage("Invalid Number");
      return;
    }

    const regex = /^[6-9]\d{9}$/;
    if (!regex.test(phoneNumber)) {
      setErrorMessage("Invalid Number");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    
    try {
      const res = await generateOtpForVerification(phoneNumber, true);
      if (!res.hasError && res.otpId) {
        setOtpId(res.otpId);
        setStep("OTP");
        setSuccessMessage("");
      } else {
        setErrorMessage(res.message || "Failed to send OTP.");
      }
    } catch (error) {
      console.log(error);
      setErrorMessage("Network error sending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!otpId) return;
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const res = await resendOtpForVerification(phoneNumber, otpId, true);
      if (!res.hasError && res.otpId) {
        setOtpId(res.otpId);
        setSuccessMessage("OTP Resent successfully");
      } else {
        setErrorMessage(res.message || "Failed to resend OTP.");
      }
    } catch (error) {
      console.log(error);
      setErrorMessage("Network error resending OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (otpString: string) => {
    if (!otpId || otpString.length < 4) return;
    
    setIsLoading(true);
    setErrorMessage("");

    try {
      const res = await verifyContactDetail(phoneNumber, otpId, otpString, true);
      if (!res.hasError) {
        window.location.reload(); 
      } else {
        setErrorMessage(res.message || "Invalid OTP entered.");
      }
    } catch (error) {
      console.log(error);
      setErrorMessage("Network error verifying OTP.");
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToPhone = () => {
    setStep("PHONE");
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleLogout = () => {
    window.location.href = `${window.location.origin}/signout`;
  };

  return {
    open,
    setOpen,
    step,
    phoneNumber,
    handlePhoneChange,
    isLoading,
    errorMessage,
    successMessage,
    sendOtp,
    resendOtp,
    verifyOtp,
    goBackToPhone,
    handleLogout,
  };
}